import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useVoteData } from "../../context/VoteDataContext";
import { useSettings } from "../../context/SettingsContext";
import SideNav from "../SideNav/SideNav";
import TeamVotesTable from "./TeamVotesTable";
import TeamTotalsTable from "./TeamTotalsTable";
// Team logos
import AdlLogo from "../../assets/team_logos/adl.svg";
import BlLogo from "../../assets/team_logos/bl.svg";
import CarlLogo from "../../assets/team_logos/carl.svg";
import CollLogo from "../../assets/team_logos/coll.svg";
import EssLogo from "../../assets/team_logos/ess.svg";
import FreLogo from "../../assets/team_logos/fre.svg";
import GeelLogo from "../../assets/team_logos/geel.svg";
import GcLogo from "../../assets/team_logos/gc.svg";
import GwsLogo from "../../assets/team_logos/gws.svg";
import HawLogo from "../../assets/team_logos/haw.svg";
import MelbLogo from "../../assets/team_logos/melb.svg";
import NmLogo from "../../assets/team_logos/nm.svg";
import PortLogo from "../../assets/team_logos/port.svg";
import RichLogo from "../../assets/team_logos/rich.svg";
import StkLogo from "../../assets/team_logos/stk.svg";
import SydLogo from "../../assets/team_logos/syd.svg";
import WceLogo from "../../assets/team_logos/wce.svg";
import WbdLogo from "../../assets/team_logos/wbd.svg";

// Team abbreviations and helper functions
const TEAM_ABBREVIATIONS = {
    "Adelaide_Crows": "ADL",
    "Brisbane_Lions": "BL", 
    "Carlton": "CARL",
    "Collingwood": "COLL",
    "Essendon": "ESS",
    "Fremantle": "FRE",
    "Geelong_Cats": "GEEL",
    "Gold_Coast_Suns": "GC",
    "GWS_Giants": "GWS",
    "Hawthorn": "HAW",
    "Melbourne": "MELB",
    "North_Melbourne": "NM",
    "Port_Adelaide": "PORT",
    "Richmond": "RICH",
    "St_Kilda": "STK",
    "Sydney_Swans": "SYD",
    "West_Coast_Eagles": "WCE",
    "Western_Bulldogs": "WBD"
};

const getTeamAbbreviation = (teamName) => {
    return TEAM_ABBREVIATIONS[teamName] || teamName;
};

const getTeamLogo = (teamName) => {
    const logos = {
        "Adelaide_Crows": AdlLogo,
        "Brisbane_Lions": BlLogo,
        "Carlton": CarlLogo,
        "Collingwood": CollLogo,
        "Essendon": EssLogo,
        "Fremantle": FreLogo,
        "Geelong_Cats": GeelLogo,
        "Gold_Coast_Suns": GcLogo,
        "GWS_Giants": GwsLogo,
        "Hawthorn": HawLogo,
        "Melbourne": MelbLogo,
        "North_Melbourne": NmLogo,
        "Port_Adelaide": PortLogo,
        "Richmond": RichLogo,
        "St_Kilda": StkLogo,
        "Sydney_Swans": SydLogo,
        "West_Coast_Eagles": WceLogo,
        "Western_Bulldogs": WbdLogo,
    };
    return logos[teamName] || null;
};

const TeamVotes = () => {
    const { user, token, isAuthenticated } = useAuth();
    const { data: voteData, isLoading: isVoteLoading, error: voteError, fetchVoteData } = useVoteData();
    const { getEnabledKeys } = useSettings();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeView, setActiveView] = useState('votes'); // 'votes' | 'totals'

    // Fetch vote data once after successful login
    useEffect(() => {
        const t = token && token();
        if (isAuthenticated && t && !voteData) {
            fetchVoteData(t);
        }
    }, [isAuthenticated, token, voteData, fetchVoteData]);

    // Get teams for navigation buttons (only in votes view)
    const getTeamsForNavigation = () => {
        if (!voteData?.leaderboard || activeView !== 'votes') return [];
        
        const enabledKeys = getEnabledKeys();
        const players = voteData.leaderboard;
        
        // Group players by team (using same logic as TeamVotesTable)
        const teamGroups = players.reduce((acc, player) => {
            const team = (player.full_team_name || player.team || "").toString();
            if (!acc[team]) acc[team] = [];
            acc[team].push(player);
            return acc;
        }, {});

        return Object.keys(teamGroups).sort();
    };

    const scrollToTeam = (teamName) => {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
            const teamId = teamName.replace(/\s+/g, '-').toLowerCase();
            
            // Check if we're on mobile or desktop by screen size
            const isMobile = window.innerWidth < 1024; // lg breakpoint is 1024px
            
            let element;
            if (isMobile) {
                // On mobile, look for mobile ID
                element = document.getElementById(`mobile-team-${teamId}`);
            } else {
                // On desktop, look for desktop ID
                element = document.getElementById(`team-${teamId}`);
            }
            
            // Fallback: try the other ID if first one not found
            if (!element) {
                if (isMobile) {
                    element = document.getElementById(`team-${teamId}`);
                } else {
                    element = document.getElementById(`mobile-team-${teamId}`);
                }
            }
            
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleViewChange = (newView) => {
        setActiveView(newView);
        // Scroll to top of the page when switching views
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const teamsForNavigation = getTeamsForNavigation();

    return (
        <div className="min-h-screen bg-[#020817] text-white">
            {/* Navigation */}
            <SideNav 
                isMobileMenuOpen={isMobileMenuOpen} 
                setIsMobileMenuOpen={setIsMobileMenuOpen} 
            />

            {/* Team Votes Content */}
            <div className="md:ml-[var(--sidenav-width)] md:transition-[margin-left] md:duration-200 md:ease-out pt-3 md:pt-6 pb-20 md:pb-0">
                {isVoteLoading && (
                    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-3 md:px-6">
                        <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-800 border-t-emerald-400" />
                    </div>
                )}
                {voteError && (
                    <div className="px-3 md:px-6 pb-6">
                        <div className="bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-xl p-3 text-sm">{voteError}</div>
                    </div>
                )}
                {!isVoteLoading && (
                    <div className="px-3 md:px-6 pb-6">
                        {/* Combined Sticky Header - Team Navigation and Toggle Pills.
                            Sticks at top-0 (the old top-14 offset assumed the removed navbar)
                            and bleeds to the page edges so content scrolls cleanly behind it. */}
                        <div className="sticky top-0 z-30 bg-[#020817]/95 backdrop-blur border-b border-slate-800/60 -mx-3 md:-mx-6 px-3 md:px-6 py-2.5 mb-3">
                            {/* Mobile Layout */}
                            <div className="lg:hidden space-y-2.5">
                                {/* Row 1: view toggle pills split the full width 50/50.
                                    (The old hamburger clearance and the team-nav collapse
                                    toggle are gone — the team nav is always shown.) */}
                                <div className="flex items-center gap-2">
                                    {/* Toggle Pills */}
                                    <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70 flex-1">
                                        <button
                                            onClick={() => handleViewChange('votes')}
                                            className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold whitespace-nowrap transition-all flex-1 ${
                                                activeView === 'votes' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 border border-transparent hover:text-slate-200'
                                            }`}
                                        >
                                            Team Votes
                                        </button>
                                        <button
                                            onClick={() => handleViewChange('totals')}
                                            className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold whitespace-nowrap transition-all flex-1 ${
                                                activeView === 'totals' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 border border-transparent hover:text-slate-200'
                                            }`}
                                        >
                                            Team Totals
                                        </button>
                                    </div>
                                </div>

                                {/* Row 2: Team Navigation Buttons - always-visible horizontal scroll */}
                                {activeView === 'votes' && teamsForNavigation.length > 0 && (
                                    <div className="overflow-x-auto scrollbar-hide">
                                        <div className="flex items-center gap-1.5 pb-0.5" style={{ minWidth: 'max-content' }}>
                                            {teamsForNavigation.map((team) => {
                                                const logo = getTeamLogo(team);
                                                return (
                                                    <button
                                                        key={`nav-${team}`}
                                                        onClick={() => scrollToTeam(team)}
                                                        title={team.replace(/_/g, ' ')}
                                                        aria-label={`Go to ${team.replace(/_/g, ' ')}`}
                                                        className="w-8 h-8 rounded-full bg-emerald-500 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-emerald-300 hover:scale-110 transition-all duration-150"
                                                    >
                                                        {logo ? (
                                                            <img 
                                                                src={logo} 
                                                                alt={`${team} logo`}
                                                                className="w-5 h-5 object-contain"
                                                            />
                                                        ) : (
                                                            <span className="text-slate-950 font-mono font-bold text-[9px]">{getTeamAbbreviation(team)}</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Desktop Layout */}
                            <div className="hidden lg:flex lg:items-center lg:justify-between gap-3">
                                {/* Team Navigation Buttons - Only show in votes view */}
                                {activeView === 'votes' && teamsForNavigation.length > 0 ? (
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap flex-shrink-0">Jump to team</span>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {teamsForNavigation.map((team) => {
                                                const logo = getTeamLogo(team);
                                                return (
                                                    <button
                                                        key={`nav-${team}`}
                                                        onClick={() => scrollToTeam(team)}
                                                        title={team.replace(/_/g, ' ')}
                                                        aria-label={`Go to ${team.replace(/_/g, ' ')}`}
                                                        className="w-8 h-8 rounded-full bg-emerald-500 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-emerald-300 hover:scale-110 transition-all duration-150"
                                                    >
                                                        {logo ? (
                                                            <img 
                                                                src={logo} 
                                                                alt={`${team} logo`}
                                                                className="w-5 h-5 object-contain"
                                                            />
                                                        ) : (
                                                            <span className="text-slate-950 font-mono font-bold text-[9px]">{getTeamAbbreviation(team)}</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                                
                                {/* Toggle Pills */}
                                <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70">
                                    <button
                                        onClick={() => handleViewChange('votes')}
                                        className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold whitespace-nowrap transition-all ${
                                            activeView === 'votes' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 border border-transparent hover:text-slate-200'
                                        }`}
                                    >
                                        Team Votes
                                    </button>
                                    <button
                                        onClick={() => handleViewChange('totals')}
                                        className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold whitespace-nowrap transition-all ${
                                            activeView === 'totals' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 border border-transparent hover:text-slate-200'
                                        }`}
                                    >
                                        Team Totals
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Content based on active view */}
                        {activeView === 'votes' ? (
            <TeamVotesTable />
                        ) : (
                            <TeamTotalsTable />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamVotes;